import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Input, InputNumber, Modal, Row, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { blockIp, getBlockedIps, getTopIps, unblockIp } from '../services/security';
import type { BlockedIp, TopIpItem } from '../services/security';

function fmtTs(ts: number | null) {
  if (!ts) return '-';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
}

function getErrorMessage(e: unknown) {
  if (typeof e === 'string') return e;
  if (e instanceof Error) return e.message || '获取失败';
  if (e && typeof e === 'object') {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return '获取失败';
}

const Security: React.FC = () => {
  const [minutes, setMinutes] = useState(1);
  const [topIpsLoading, setTopIpsLoading] = useState(false);
  const [topIpsError, setTopIpsError] = useState<string | null>(null);
  const [topIps, setTopIps] = useState<TopIpItem[]>([]);

  const [blockedLoading, setBlockedLoading] = useState(false);
  const [blockedError, setBlockedError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<BlockedIp[]>([]);
  const [blockedTotal, setBlockedTotal] = useState(0);
  const [blockedPage, setBlockedPage] = useState(1);
  const [blockedLimit, setBlockedLimit] = useState(50);
  const [ipQuery, setIpQuery] = useState('');

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockForm] = Form.useForm();

  const refreshTopIps = async () => {
    setTopIpsLoading(true);
    try {
      const res = await getTopIps({ minutes, limit: 50 });
      setTopIps(res.items);
      message.info(`读取日志：${res.file}（扫描${res.scannedLines}行，命中${res.matchedLines}行）`, 2);
      setTopIpsError(null);
    } catch (e: unknown) {
      setTopIpsError(getErrorMessage(e));
    } finally {
      setTopIpsLoading(false);
    }
  };

  const refreshBlocked = async (page = blockedPage, limit = blockedLimit, ip = ipQuery) => {
    setBlockedLoading(true);
    try {
      const res = await getBlockedIps({ page, limit, ip: ip || undefined });
      setBlocked(res.items);
      setBlockedTotal(res.total);
      setBlockedPage(res.page);
      setBlockedLimit(res.limit);
      setBlockedError(null);
    } catch (e: unknown) {
      setBlockedError(getErrorMessage(e));
    } finally {
      setBlockedLoading(false);
    }
  };

  useEffect(() => {
    void refreshTopIps();
    void refreshBlocked(1, blockedLimit, ipQuery);
  }, []);

  const topIpColumns: ColumnsType<TopIpItem> = useMemo(
    () => [
      { title: 'IP', dataIndex: 'ip', key: 'ip', width: 160 },
      { title: '请求数', dataIndex: 'total', key: 'total', width: 90, sorter: (a, b) => a.total - b.total, defaultSortOrder: 'descend' },
      {
        title: '异常',
        key: 'bad',
        width: 180,
        render: (_, r) => (
          <Space size={6}>
            {r.status429 > 0 ? (
              <Tooltip title="429：请求过于频繁（触发限流）">
                <Tag color="orange">429 {r.status429}</Tag>
              </Tooltip>
            ) : null}
            {r.status401 > 0 ? (
              <Tooltip title="401：未授权/登录失效或访问受限">
                <Tag color="red">401 {r.status401}</Tag>
              </Tooltip>
            ) : null}
            {r.status5xx > 0 ? (
              <Tooltip title="5xx：服务端异常（500-599）">
                <Tag color="magenta">5xx {r.status5xx}</Tag>
              </Tooltip>
            ) : null}
          </Space>
        ),
      },
      { title: '首次出现', key: 'firstSeen', width: 200, render: (_, r) => fmtTs(r.firstSeen) },
      { title: '最近出现', key: 'lastSeen', width: 200, render: (_, r) => fmtTs(r.lastSeen) },
      {
        title: 'Top 路径',
        key: 'topUrls',
        render: (_, r) => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {r.topUrls.slice(0, 5).map((u) => (
              <Tag key={u.url}>
                {u.count} {u.url}
              </Tag>
            ))}
          </div>
        ),
      },
      {
        title: '操作',
        key: 'action',
        width: 140,
        fixed: 'right',
        render: (_, r) => (
          <Space>
            <Button
              size="small"
              danger
              onClick={() => {
                blockForm.setFieldsValue({ ip: r.ip, reason: `日志频繁访问（${minutes}分钟内 ${r.total}次）` });
                setBlockModalOpen(true);
              }}
            >
              拉黑
            </Button>
          </Space>
        ),
      },
    ],
    [minutes],
  );

  const blockedColumns: ColumnsType<BlockedIp> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
      { title: 'IP', dataIndex: 'ip', key: 'ip', width: 160 },
      { title: '原因', dataIndex: 'reason', key: 'reason' },
      {
        title: '到期时间',
        dataIndex: 'expiresAt',
        key: 'expiresAt',
        width: 200,
        render: (v) => (v ? new Date(v).toLocaleString() : '永久'),
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 200,
        render: (v) => new Date(v).toLocaleString(),
      },
      {
        title: '操作',
        key: 'action',
        width: 140,
        fixed: 'right',
        render: (_, r) => (
          <Button
            size="small"
            onClick={async () => {
              await unblockIp(r.id);
              message.success('已解除');
              await refreshBlocked(1, blockedLimit, ipQuery);
            }}
          >
            解除
          </Button>
        ),
      },
    ],
    [blockedLimit, ipQuery],
  );

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>安全分析与 IP 拉黑</h2>

      <Row gutter={16}>
        <Col span={24}>
          <Card
            title="PM2 日志 Top IP"
            extra={
              <Space>
                <span>时间窗口(分钟)</span>
                <InputNumber min={1} max={10080} value={minutes} onChange={(v) => setMinutes(Number(v || 1))} />
                <Button loading={topIpsLoading} onClick={refreshTopIps}>
                  刷新
                </Button>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <div style={{ marginBottom: 12 }}>
              <Alert
                type="info"
                showIcon
                message="状态码说明"
                description="异常列为该 IP 在时间窗口内出现的次数：429=请求过于频繁（触发限流）；401=未授权/登录失效或访问受限；5xx=服务端异常（500-599）。"
              />
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                数据来源：PM2 日志分析（按时间窗口扫描）
              </Typography.Text>
            </div>
            {topIpsError ? (
              <Alert
                type="error"
                showIcon
                message="获取失败"
                description={
                  <div>
                    <div>{topIpsError}</div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                      提示：请确认已登录管理员账号、后端可访问，且服务器 PM2 日志读取配置正常。
                    </Typography.Text>
                  </div>
                }
                style={{ marginBottom: 12 }}
              />
            ) : null}
            <Table
              rowKey="ip"
              columns={topIpColumns}
              dataSource={topIps}
              loading={topIpsLoading}
              pagination={false}
              scroll={{ x: 1200 }}
              size="small"
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title="已拉黑 IP"
            extra={
              <Space>
                <Input
                  allowClear
                  placeholder="搜索 IP"
                  value={ipQuery}
                  onChange={(e) => setIpQuery(e.target.value)}
                  onPressEnter={() => void refreshBlocked(1, blockedLimit, ipQuery)}
                  style={{ width: 240 }}
                />
                <Button onClick={() => void refreshBlocked(1, blockedLimit, ipQuery)} loading={blockedLoading}>
                  查询
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    blockForm.resetFields();
                    setBlockModalOpen(true);
                  }}
                >
                  手动拉黑
                </Button>
              </Space>
            }
          >
            {blockedError ? <Alert type="error" showIcon message="获取失败" description={blockedError} style={{ marginBottom: 12 }} /> : null}
            <Table
              rowKey="id"
              columns={blockedColumns}
              dataSource={blocked}
              loading={blockedLoading}
              pagination={{
                current: blockedPage,
                pageSize: blockedLimit,
                total: blockedTotal,
                showSizeChanger: true,
                onChange: (p, s) => void refreshBlocked(p, s, ipQuery),
              }}
              scroll={{ x: 1000 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Modal
        open={blockModalOpen}
        title="拉黑 IP"
        okText="确认"
        cancelText="取消"
        onCancel={() => setBlockModalOpen(false)}
        onOk={async () => {
          const v = await blockForm.validateFields();
          await blockIp({
            ip: v.ip,
            reason: v.reason,
            expiresAt: v.expiresAt ? new Date(v.expiresAt).toISOString() : undefined,
          });
          message.success('已拉黑');
          setBlockModalOpen(false);
          await refreshBlocked(1, blockedLimit, ipQuery);
        }}
      >
        <Form form={blockForm} layout="vertical">
          <Form.Item name="ip" label="IP" rules={[{ required: true, message: '请输入 IP' }]}>
            <Input placeholder="例如 1.2.3.4" />
          </Form.Item>
          <Form.Item name="reason" label="原因">
            <Input placeholder="可选" />
          </Form.Item>
          <Form.Item name="expiresAt" label="到期时间(可选)">
            <Input placeholder="例如 2026-03-23T12:00:00Z" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Security;
